import { useV2GetAccountQuery, useV2GetProjectByIdQuery } from "@generated";
import { renderResourceOptions } from "@utils/helperFunctions";
import { SearchSelectGroup } from "@components/FormsV2/SearchSelect";
import {
  projectRateTableId,
  useListProjectResources,
  useListResourceRates,
  useListResources,
} from "../../pages/Projects/ServicesV2/api";

interface ProjectResourceOptionsInput {
  projectResources: any[];
  resources: any[];
  resourceRates: any[];
  useResourceRates: boolean;
  vendorResourceLabel: string;
}

// Project, vendor and standard resource groups, built the same way as the
// project editor's resource column. Values carry their type
// ("project-resources-1", "resources-7", "resource-rates-9"); the API accepts
// any of them as the project-resource id and creates the project resource
// when a standard resource is picked.
export const projectResourceOptions = ({
  projectResources,
  resources,
  resourceRates,
  useResourceRates,
  vendorResourceLabel,
}: ProjectResourceOptionsInput): SearchSelectGroup[] =>
  renderResourceOptions(
    projectResources,
    useResourceRates ? resourceRates : resources,
    [],
    vendorResourceLabel
  )
    // Drop the "Select Resource" placeholder; keep just the groups.
    .filter((opt) => opt.options)
    .map(({ label, options }) => ({ label, options: options! }));

// Uses the same queries (and so the same cache entries) as the project editor.
const useProjectResourceOptions = (projectId: string) => {
  const { data: account } = useV2GetAccountQuery({});
  const { data: project } = useV2GetProjectByIdQuery({
    id: projectId,
    include: ["rate-table"],
  });
  const { projectResources, projectResourcesLoading } =
    useListProjectResources(projectId);
  const { resources, resourcesLoading } = useListResources();
  const { resourceRates, resourceRatesLoading } = useListResourceRates(
    projectRateTableId(project)
  );

  const attributes: any = account?.data?.attributes;
  const useResourceRates = !!attributes?.settings?.project_resource_rates;

  return {
    options: projectResourceOptions({
      projectResources,
      resources,
      resourceRates,
      useResourceRates,
      vendorResourceLabel:
        attributes?.["field-labels"]?.["vendor_resource"] || "Vendor Resource",
    }),
    isLoading:
      projectResourcesLoading ||
      (useResourceRates ? resourceRatesLoading : resourcesLoading),
  };
};

export default useProjectResourceOptions;
